import { type AxiosInstance } from 'axios';
import { z } from 'zod';

import { FeatureFlagKey, type IFeatureFlag } from '../constants';
import {
  Logger,
  getFeatureFlagFromCache,
  getFeatureFlagsFromCache,
  saveFeatureFlagsToCache,
} from '../utils';

const booleanish = z.union([z.boolean(), z.literal(0), z.literal(1)]);

const featureFlagsBulkResponseSchema = z.object({
  features: z.record(z.string(), booleanish),
});

const normalizeFeatureValue = (raw: boolean | 0 | 1): boolean =>
  raw === true || raw === 1;

const parseFeatureFlagsBulkPayload = (
  data: unknown
): Partial<Record<FeatureFlagKey, boolean>> => {
  const parsed = featureFlagsBulkResponseSchema.safeParse(data);
  if (!parsed.success) {
    Logger.error('Invalid feature flags API payload', parsed.error);
    throw new Error('Invalid feature flags response');
  }

  const features: Partial<Record<FeatureFlagKey, boolean>> = {};
  for (const [key, raw] of Object.entries(parsed.data.features)) {
    if (!Object.values(FeatureFlagKey).includes(key as FeatureFlagKey)) {
      continue;
    }
    features[key as FeatureFlagKey] = normalizeFeatureValue(raw);
  }

  return features;
};

let inFlightFetch: Promise<Partial<Record<FeatureFlagKey, boolean>>> | null =
  null;

export const fetchAllFeatureFlags = (
  api: AxiosInstance
): Promise<Partial<Record<FeatureFlagKey, boolean>>> => {
  if (inFlightFetch) {
    return inFlightFetch;
  }

  inFlightFetch = api
    .get('/api/features/status')
    .then(({ data }) => {
      const normalized = parseFeatureFlagsBulkPayload(data);
      saveFeatureFlagsToCache(normalized);
      return normalized;
    })
    .catch((error) => {
      Logger.error('Error fetching feature flags from API:', error);
      throw error;
    })
    .finally(() => {
      inFlightFetch = null;
    });

  return inFlightFetch;
};

export const prefetchFeatureFlags = (api: AxiosInstance): void => {
  if (getFeatureFlagsFromCache()) {
    return;
  }

  void fetchAllFeatureFlags(api).catch(() => {
    // error already logged in fetchAllFeatureFlags
  });
};

export const getFeatureFlag = (
  api: AxiosInstance,
  flagKey: FeatureFlagKey
): Promise<IFeatureFlag> => {
  const cachedFlag = getFeatureFlagFromCache(flagKey);
  if (cachedFlag) {
    return Promise.resolve(cachedFlag);
  }

  return fetchAllFeatureFlags(api).then((features) => ({
    feature: flagKey,
    active: features[flagKey] ?? false,
  }));
};
